import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useReseller } from '../../contexts/ResellerContext';
import Cookies from '../../common/Cookies';

const SCRIPT_ATTR = 'data-chattitude-agent-widget';
const WIDGET_IFRAME_TITLE = 'Chattitude Agent';

/** Guest routes that are not the Login screen (Login is the catch-all when logged out). */
const GUEST_NON_LOGIN_PATHS = [
  /^\/Signup$/i,
  /^\/signup\/form/i,
  /^\/invite/i,
  /^\/reset-password/i,
  /^\/social-login\//i,
  /^\/public\/chatbot-frame/i,
];

function widgetUrlFromReseller(reseller) {
  const bu = (reseller?.baseUrl && String(reseller.baseUrl).trim()) || '';
  if (/^https?:\/\//i.test(bu)) {
    try {
      return new URL(bu).origin;
    } catch {
      /* ignore */
    }
  }
  return window.location.origin;
}

/** True on explicit /Login and on logged-out catch-all routes that render Login. */
function isLoginPage(pathname) {
  if (/^\/Login$/i.test(pathname)) return true;
  const token = Cookies.getJwtToken();
  const loggedIn = token && String(token).length > 0;
  if (loggedIn) return false;
  return !GUEST_NON_LOGIN_PATHS.some((re) => re.test(pathname));
}

/** Agent detail / edit pages: #/Agent, #/Agent/:id */
function isAgentPage(pathname) {
  return /^\/Agent(\/|$)/i.test(pathname);
}

/** Routes where the floating help chat must not appear. */
function shouldHideWidget(pathname) {
  return isLoginPage(pathname) || isAgentPage(pathname);
}

function setWidgetVisible(visible) {
  const iframe = document.querySelector(`iframe[title="${WIDGET_IFRAME_TITLE}"]`);
  if (!iframe) return;
  iframe.style.display = visible ? '' : 'none';
}

/**
 * Loads the floating Chattitude widget when the resolved reseller has
 * {@code chatbotAgent} and {@code chatbotTenant} (from API / cookie via ResellerContext).
 * Hidden on Login and Agent pages.
 */
export default function ResellerChatbotWidget() {
  const { reseller, resellerResolved } = useReseller();
  const location = useLocation();
  const injectedRef = useRef(false);
  const hideWidget = shouldHideWidget(location.pathname);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('mode') === 'chat') return;
    if (location.pathname === '/public/chatbot-frame') return;

    // Keep visibility in sync when navigating to/from hidden routes after inject
    if (injectedRef.current || document.querySelector(`iframe[title="${WIDGET_IFRAME_TITLE}"]`)) {
      setWidgetVisible(!hideWidget);
    }

    if (hideWidget) return;
    if (!resellerResolved) return;
    if (!reseller?.chatbotAgent || !reseller?.chatbotTenant) return;
    if (injectedRef.current) return;
    if (document.querySelector(`script[${SCRIPT_ATTR}]`)) {
      injectedRef.current = true;
      setWidgetVisible(true);
      return;
    }

    const widgetUrl = widgetUrlFromReseller(reseller);
    window.slickbotsConfig = {
      agent: reseller.chatbotAgent,
      tenant: reseller.chatbotTenant,
      widgetUrl,
    };
    const s = document.createElement('script');
    s.setAttribute(SCRIPT_ATTR, '1');
    s.src = `${widgetUrl.replace(/\/+$/, '')}/widget.js`;
    s.async = true;
    document.head.appendChild(s);
    injectedRef.current = true;
  }, [reseller, resellerResolved, location.pathname, hideWidget]);

  return null;
}
